import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select } from "../ui/select";
import { MultiSelect } from "../ui/multi-select";
import { DollarSign, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import { updateTransfer } from "../../services/crmServices/transferService";
import { searchUsers, fetchUsers } from "../../services/crmServices/userService";
import { getFullName } from "../../services/common/util";
import { useAuth } from "../../hooks/useAuth";
import moment from "moment-timezone";

export function ChargeModal({ open, onClose, onSuccess, transfer }) {
    const { user } = useAuth();
    const [chargeAmount, setChargeAmount] = useState("");
    const [chargeNote, setChargeNote] = useState("");
    const [chargedByAgent, setChargedByAgent] = useState("");
    const [isSplit, setIsSplit] = useState(false);
    const [splitAgents, setSplitAgents] = useState([]);
    const [availableAgents, setAvailableAgents] = useState([]);
    const [loading, setLoading] = useState(false);
    
    const existingCharges = transfer?.charges || [];
    const totalCharges = existingCharges.reduce((sum, charge) => sum + (charge.amount || 0), 0);

    // Fetch all agents when modal opens
    useEffect(() => {
        if (open) {
            // Get ALL users then filter to agents
            fetchUsers().then((res) => {
                if (res?.data) {
                    // Filter to only show agents (not admins, managers, etc.)
                    const agents = res.data.filter(u => u.position?.role === 'agent');
                    setAvailableAgents(agents);
                }
            }).catch(err => {
                console.error("Error loading agents:", err);
                toast.error("Failed to load agents");
            });
        }
    }, [open]);

    const handleSave = async () => {
        if (!chargeAmount || parseFloat(chargeAmount) <= 0) {
            toast.error("Please enter a valid charge amount");
            return;
        }

        if (!chargedByAgent) {
            toast.error("Please select which agent charged the client");
            return;
        }

        if (isSplit && (!splitAgents || splitAgents.length === 0)) {
            toast.error("Please select at least one agent for split charge");
            return;
        }

        setLoading(true);

        try {
            const newCharge = {
                amount: parseFloat(chargeAmount),
                chargedBy: chargedByAgent, // The retention agent who charged
                chargedAt: moment().tz("America/New_York").toDate(),
                note: chargeNote.trim() || null,
                isSplit: isSplit,
                splitAgents: isSplit ? splitAgents : []
            };

            const updateData = {
                _id: transfer._id,
                charges: [...existingCharges, newCharge]
            };

            const response = await updateTransfer(updateData);

            if (response?.status === 200) {
                toast.success("Charge added successfully!");
                setChargeAmount("");
                setChargeNote("");
                setChargedByAgent("");
                setIsSplit(false);
                setSplitAgents([]);
                onSuccess();
            } else {
                toast.error(response?.data?.message || "Failed to add charge");
            }
        } catch (error) {
            console.error("Charge save error:", error);
            toast.error("Failed to add charge");
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setChargeAmount("");
        setChargeNote("");
        setChargedByAgent("");
        setIsSplit(false);
        setSplitAgents([]);
        onClose();
    };

    if (!transfer) return null;

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center">
                        <DollarSign className="h-5 w-5 mr-2" />
                        Add Transfer Charge
                    </DialogTitle>
                    <DialogDescription>
                        Add charge for transfer of {getFullName(transfer.lead)}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium mb-2">Transfer Details</h4>
                        <div className="text-sm space-y-1">
                            <p>
                                <strong>Lead:</strong> {getFullName(transfer.lead)}
                            </p>
                            <p>
                                <strong>From Agent:</strong> {getFullName(transfer.requestedBy)}
                            </p>
                            <p>
                                <strong>To Desk:</strong> {transfer.targetDesk?.name || "-"}
                            </p>
                            <p>
                                <strong>To Agent:</strong> {transfer.targetAgent ? getFullName(transfer.targetAgent) : "-"}
                            </p>
                        </div>
                    </div>

                    {/* Existing Charges */}
                    {existingCharges.length > 0 && (
                        <div className="bg-gray-50 p-4 rounded-lg">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="font-medium">Existing Charges</h4>
                                <span className="text-lg font-bold text-green-600">
                                    Total: ${totalCharges.toLocaleString()}
                                </span>
                            </div>
                            <div className="space-y-2">
                                {existingCharges.map((charge, index) => (
                                    <div key={index} className="flex items-center justify-between p-2 bg-white rounded border">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-green-600">
                                                    ${charge.amount?.toLocaleString()}
                                                </span>
                                                {charge.isSplit && charge.splitAgents?.length > 0 ? (
                                                    <>
                                                        <Users className="h-3 w-3 text-blue-600" />
                                                        <span className="text-xs text-blue-600 font-medium">
                                                            Split: {charge.splitAgents.map(a => getFullName(a)).join(", ")}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="text-xs text-gray-500">
                                                        by {charge.chargedBy ? getFullName(charge.chargedBy) : "Unknown"}
                                                    </span>
                                                )}
                                                <span className="text-xs text-gray-400">
                                                    {moment(charge.chargedAt).format("MMM D, YYYY HH:mm")}
                                                </span>
                                            </div>
                                            {charge.note && (
                                                <p className="text-xs text-gray-600 mt-1">{charge.note}</p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Add New Charge */}
                    <div className="border-t pt-4">
                        <h4 className="font-medium mb-3">Add New Charge</h4>
                        
                        <div className="space-y-4">
                            <div>
                                <Label htmlFor="chargeAmount">Charge Amount *</Label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                                        $
                                    </span>
                                    <Input
                                        id="chargeAmount"
                                        type="number"
                                        value={chargeAmount}
                                        onChange={(e) => setChargeAmount(e.target.value)}
                                        placeholder="e.g., 500"
                                        min="0"
                                        step="0.01"
                                        required
                                        className="pl-7"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="chargedByAgent">Charged By Agent *</Label>
                                <Select
                                    options={availableAgents.map(agent => ({
                                        value: agent._id,
                                        label: getFullName(agent)
                                    }))}
                                    value={chargedByAgent}
                                    onChange={(value) => setChargedByAgent(value)}
                                    searchable={true}
                                    searchPlaceholder="Search agents..."
                                    placeholder="Select agent who charged..."
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    Which retention agent charged this client?
                                </p>
                            </div>

                            <div>
                                <Label htmlFor="chargeNote">Note (Optional)</Label>
                                <Textarea
                                    id="chargeNote"
                                    value={chargeNote}
                                    onChange={(e) => setChargeNote(e.target.value)}
                                    placeholder="Add a note about this charge..."
                                    rows={2}
                                />
                            </div>

                            <div className="border-t pt-3">
                                <div className="flex items-center justify-between mb-3">
                                    <Label className="mb-0">Split Charge</Label>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={isSplit ? "default" : "outline"}
                                        onClick={() => {
                                            setIsSplit(!isSplit);
                                            if (isSplit) setSplitAgents([]);
                                        }}
                                    >
                                        <Users className="h-4 w-4 mr-1" />
                                        {isSplit ? "Split Active" : "Enable Split"}
                                    </Button>
                                </div>

                                {isSplit && (
                                    <div>
                                        <Label htmlFor="splitAgents">Select Agents for Split</Label>
                                        <MultiSelect
                                            options={availableAgents.map(agent => ({
                                                value: agent._id,
                                                label: getFullName(agent)
                                            }))}
                                            selected={splitAgents}
                                            onChange={(values) => setSplitAgents(values)}
                                            searchPlaceholder="Search agents..."
                                            placeholder="Select agents..."
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            Select agents who will split this charge
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="flex space-x-3">
                        <Button
                            onClick={handleSave}
                            disabled={loading || !chargeAmount}
                            className="flex-1 "
                        >
                            {loading ? "Saving..." : "Save Charge"}
                        </Button>
                        <Button variant="outline" onClick={handleClose} disabled={loading} className="flex-1">
                            Cancel
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
